import { Elastic, Power1, TweenMax } from 'gsap';
import { GameAudio } from '../../../Config/Audio';
import Core from '../../../Core/Core';
import { TGameAreas, TransactionActionTypes } from '../../../DeltaTracking/ResourceParamBuilder';
import { HudActions, HudEmitter } from '../../../Managers/Hud/HudEmitter';
import { Popup } from '../../../Managers/Popups';
import SoundManager from '../../../Sound/SoundManager';
import { IRewardItem, storeRewards } from '../../../State/reducers/score';
import { RewardSparkle } from '../../Animations/RewardSparkle';
import { SkippableAnimation } from '../../Animations/SkippableAnimation';
import { AdButton } from '../../Shared/Buttons/AdButton';
import { TextButton } from '../../Shared/Buttons/TextButton';
import { UIButton } from '../../Shared/Buttons/UIButton';
import { RewardGroup } from './RewardGroup';
import ResourceTracking from '../../../Managers/Tracking/ResourceTracking';
import { LevelActions, LevelEmitter } from '../../../Managers/Level/LevelEmitter';
import FTUETracking, { FTUE_TRACK_STAGES } from '../../../Managers/Tracking/FTUETracking';
import AdTracking, { VideoAdReasons } from '../../../Managers/Tracking/AdTracking';
import { AdContexts, AdGameAreas, AdRewardTypes } from '../../../DeltaTracking/AdParamBuilder';

export class RewardBox extends PIXI.Container {
    private box: PIXI.Sprite;
    private boxType: string;
    private btn: UIButton;
    private boxContents: IRewardItem[];
    private loadText: PIXI.Text;
    private openAnimation: SkippableAnimation;
    private collectableInProgress: string;
    private collectableProgress: number;
    private isBotReward: boolean;

    constructor(
        contents: IRewardItem[],
        type: string,
        collectableInProgress: string,
        collectableProgress: number,
        isBotReward: boolean = false
    ) {
        super();
        this.collectableInProgress = collectableInProgress;
        this.collectableProgress = collectableProgress;
        this.boxContents = contents;
        this.boxType = type;
        const boxId = type === 'ad' ? 'gameui/giftBoxAd_closed' : 'commonui/giftbox-closed';
        this.box = new PIXI.Sprite(PIXI.utils.TextureCache[boxId]);
        this.box.anchor.set(0.5, 1);
        this.box.y = this.box.height * 0.7;
        this.isBotReward = isBotReward;

        const button =
            type === 'ad'
                ? new AdButton(
                      this.box.width * 1.1,
                      this.box.height * 1.1,
                      Core.local.getText('watch')
                  )
                : new TextButton({
                      width: this.box.width,
                      height: this.box.height,
                      btnText: Core.local.getText('open'),
                  });
        button.y = this.box.height;
        button.alpha = 0;
        this.btn = button;

        //@ts-ignore
        this.addChild(this.box, this.btn);
        this.createOpenAnimation();
    }

    public showFX() {}

    public openBox() {
        this.btn.interactive = false;
        this.openAnimation.play().catch(err => {
            console.log(['error with open animation play', err]);
        });
    }

    public dispatchAdWatch() {
        TweenMax.fromTo(
            this.box,
            1,
            { rotation: 0 },
            {
                delay: 0.5,
                ease: Elastic.easeOut.config(5, 0.1),
                repeat: -1,
                rotation: 2 * (Math.PI / 180),
            }
        );

        const success = () => {
            this.openBox();
            HudEmitter.instance.emit(
                HudActions.RECEIVE_REWARDS,
                this.boxContents,
                TransactionActionTypes.WIN_SEQUENCE
            );
            LevelEmitter.instance.emit(LevelActions.PRESENT_AD_COMPLETE);

            Core.delta.setAdParams(
                AdRewardTypes.EXTRA_GIFTS,
                AdContexts.WIN_SEQUENCE,
                AdGameAreas.LEVEL,
                Core.store.getState().StateLevel.levelIndex
            );
            FTUETracking.track(FTUE_TRACK_STAGES.WIN_AD, Core.store.getState());
            AdTracking.trackAdWatched(VideoAdReasons.WIN_PRESENT);
        };

        const error = () => {
            LevelEmitter.instance.emit(LevelActions.PRESENT_AD_COMPLETE);
            TweenMax.killTweensOf(this.box);
        };

        HudEmitter.instance.emit(HudActions.WATCH_REWARDED_AD, 'rewardedPresent', success, error);
        LevelEmitter.instance.emit(HudActions.WATCH_PRESENT_AD);
    }

    public initBox(watch?: boolean) {
        TweenMax.to(this.btn, 0.3, {
            alpha: 1,
            onComplete: () => {
                if (watch) {
                    this.btn.once('trigger', this.dispatchAdWatch.bind(this));
                } else {
                    this.btn.once('trigger', this.openBox.bind(this));
                }
            },
        });
    }

    private createOpenAnimation() {
        const anim = (this.openAnimation = new SkippableAnimation());
        const openId = this.boxType === 'ad' ? 'gameui/giftBoxAd_open' : 'commonui/giftbox-open';
        const openTexture = PIXI.utils.TextureCache[openId];
        anim.addToLast(TweenMax.to(this.btn, 0.3, { alpha: 0 }));
        anim.addToLast(
            TweenMax.to(this.box.scale, 0.3, {
                y: 0.9,
                repeat: 1,
                yoyo: true,
            })
        );
        anim.add(() => {
            this.box.texture = openTexture;
            const r = new RewardSparkle(2);
            r.anchor.set(0.5);
            this.addChild(r);
            r.play();
        });
        anim.add(() => {
            if (this.loadText) {
                this.loadText.alpha = 0;
            }
            TweenMax.killTweensOf(this.box);
            this.box.rotation = 0;
        });
        const rewards: RewardGroup[] = [];
        const rewardPositions = this.getShowPositions(this.boxContents.length);

        anim.add(() => SoundManager.playSound(GameAudio.REWARD_OPEN));
        this.boxContents.forEach((reward, index) => {
            const rewardGroup = new RewardGroup(
                reward.id,
                reward.val,
                this.collectableInProgress,
                this.collectableProgress
            );
            rewards.push(rewardGroup);
            rewardGroup.x = this.box.x;
            rewardGroup.y = this.box.y - this.box.height;

            const xPos = rewardPositions[index];
            const xPos2 = xPos < this.box.x ? xPos * 0.9 : xPos > this.box.x ? xPos * 1.1 : xPos;
            let yPos = this.box.y + this.box.height * 0.15;
            rewardGroup.scale.set(0);

            this.addChild(rewardGroup);
            if (this.boxType === 'ad') {
                anim.addToLast(TweenMax.to(rewardGroup.scale, 0.5, { x: 1, y: 1 }));
                yPos = this.box.y + this.box.height * 0.1;
            } else {
                anim.addToLast(TweenMax.to(rewardGroup.scale, 0.5, { x: 0.8, y: 0.8 }));
            }
            // added bezier function
            anim.addToLast(
                TweenMax.to(rewardGroup, 0.35, {
                    bezier: {
                        type: 'soft',
                        values: [
                            { x: rewardGroup.x, y: rewardGroup.y },
                            { x: xPos2, y: 0 },
                            { x: xPos, y: yPos },
                        ],
                    },
                    ease: Power1.easeIn,
                })
            );
        });
        anim.addToLast(
            TweenMax.to(this.box.scale, 0.3, {
                y: 1.2,
                repeat: 1,
                yoyo: true,
            })
        );

        rewards.forEach(reward => {
            anim.addDelay(100);
            anim.add(() => {
                reward.showFX();
            });
        });

        anim.add(() => {
            if (this.isBotReward) {
                storeRewards(this.boxContents, TransactionActionTypes.BOT);
                HudEmitter.instance.emit(HudActions.CLOSE_POPUP, Popup.BOTREWARD, -1);
                this.boxContents.forEach(r => {
                    ResourceTracking.sendRewardResourceEvent(
                        r.id,
                        r.val as number,
                        TransactionActionTypes.BOT,
                        TGameAreas.MAP
                    );
                });
            } else {
                if (this.boxType !== 'ad') {
                    HudEmitter.instance.emit(
                        HudActions.OPEN_REWARD_BOX,
                        this.boxContents,
                        TransactionActionTypes.WIN_SEQUENCE
                    );
                }
            }
        });
    }

    private getShowPositions(numRewards: number): number[] {
        const spreadDistance = this.box.width * 2 * ((numRewards + 1) / 10);
        const spreadSpace = (spreadDistance * 2) / numRewards;
        let startPosition = this.box.x - Math.floor(numRewards / 2) * spreadSpace;
        if (numRewards % 2 === 0) {
            startPosition += spreadSpace / 2;
        }
        const positions: number[] = [];
        while (positions.length < numRewards) {
            const x = startPosition + spreadSpace * positions.length;
            positions.push(x);
        }

        return positions;
    }
}
